import { PulseStorage, PulseStorageOptions, StorageChange } from '@pulse/common';
import { Subject, fromEvent } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { PulseEnvironment } from '../../globals';
import { Quote } from './quote';

function isNativeStorage(area: Storage): area is Storage {
    return (
        typeof (area as Storage).setItem === 'function' &&
        typeof (area as Storage).getItem === 'function' &&
        typeof (area as Storage).removeItem === 'function' &&
        typeof (area as Storage).clear === 'function'
    );
}

function noop(): void {
    // Intentionally empty for non-Storage backends
}

function isPulseStorageBackend(area: unknown): area is PulseStorage {
    const p = area as Partial<PulseStorage>;

    return (
        typeof p?.get === 'function' &&
        typeof p?.set === 'function' &&
        typeof p?.remove === 'function' &&
        typeof p?.keys === 'function'
    );
}

type StorageHookListener = {
    storageArea: Storage;
    onSet: Storage['setItem'];
    onRemove: Storage['removeItem'];
    keysForClear: () => string[];
    onClearKey: Storage['removeItem'];
};

type StorageHookState = {
    originalSetItem: Storage['setItem'];
    originalRemoveItem: Storage['removeItem'];
    originalClear: Storage['clear'];
    listeners: Set<StorageHookListener>;
};

export class PulseSyncStorage extends PulseStorage {
    freeSpace$: Subject<void> = new Subject();
    private static readonly storageHookStateByPrototype = new WeakMap<object, StorageHookState>();
    private availableSpace: number | null | undefined = undefined;
    private suppressHookEvents = 0;
    private readonly originalSetItem: Storage['setItem'] | (() => void);
    private readonly originalRemoveItem: Storage['removeItem'] | (() => void);
    private readonly isNativeStorage: boolean;
    private readonly pulseStorageBackend: PulseStorage | null;

    constructor(
        env: PulseEnvironment,
        private storageArea: Storage,
        private quote: Quote,
        config: PulseStorageOptions,
    ) {
        super(config);

        this.isNativeStorage = isNativeStorage(storageArea);
        this.pulseStorageBackend = isPulseStorageBackend(storageArea)
            ? (storageArea as PulseStorage)
            : null;

        if (this.isNativeStorage) {
            const storagePrototype = Object.getPrototypeOf(storageArea) as Storage;
            const existingHookState =
                PulseSyncStorage.storageHookStateByPrototype.get(storagePrototype);
            const hookState: StorageHookState = existingHookState ?? {
                originalSetItem: storagePrototype.setItem,
                originalRemoveItem: storagePrototype.removeItem,
                originalClear: storagePrototype.clear,
                listeners: new Set(),
            };

            if (!existingHookState) {
                storagePrototype.setItem = function (
                    this: Storage,
                    key: string,
                    value: string,
                ): void {
                    hookState.originalSetItem.call(this, key, value);
                    hookState.listeners.forEach((listener) => {
                        if (listener.storageArea === this) {
                            listener.onSet(key, value);
                        }
                    });
                };
                storagePrototype.removeItem = function (this: Storage, key: string): void {
                    hookState.originalRemoveItem.call(this, key);
                    hookState.listeners.forEach((listener) => {
                        if (listener.storageArea === this) {
                            listener.onRemove(key);
                        }
                    });
                };
                storagePrototype.clear = function (): void {
                    const clearPayloads = Array.from(hookState.listeners)
                        .filter((listener) => listener.storageArea === this)
                        .map((listener) => ({
                            listener,
                            keys: listener.keysForClear(),
                        }));

                    hookState.originalClear.call(this);
                    clearPayloads.forEach(({ listener, keys: listenerKeys }) => {
                        listenerKeys.forEach((key) => listener.onClearKey(key));
                    });
                };
                PulseSyncStorage.storageHookStateByPrototype.set(storagePrototype, hookState);
            }

            const isStorageKey = this.isStorageKey.bind(this);
            const emitChange = this.emitChange.bind(this);
            const revertStorageKey = this.revertStorageKey.bind(this);
            const keys = this.keys.bind(this);
            const hookListener: StorageHookListener = {
                storageArea,
                onSet: (key: string, value: string): void => {
                    if (this.suppressHookEvents > 0) {
                        return;
                    }
                    if (!isStorageKey(key)) {
                        return;
                    }
                    emitChange({
                        key: revertStorageKey(key),
                        value,
                        external: true,
                    });
                },
                onRemove: (key: string): void => {
                    if (this.suppressHookEvents > 0) {
                        return;
                    }
                    if (!isStorageKey(key)) {
                        return;
                    }
                    emitChange({
                        key: revertStorageKey(key),
                        value: null,
                        external: true,
                    });
                },
                keysForClear: (): string[] => keys(),
                onClearKey: (key: string): void => {
                    if (this.suppressHookEvents > 0) {
                        return;
                    }
                    emitChange({ key, value: null, external: true });
                },
            };
            hookState.listeners.add(hookListener);
            this.originalSetItem = storageArea.setItem.bind(storageArea);
            this.originalRemoveItem = storageArea.removeItem.bind(storageArea);

            fromEvent(env.win, 'storage')
                .pipe(
                    filter(
                        (e): e is StorageEvent => Boolean(e) && 'key' in e && 'storageArea' in e,
                    ),
                    filter(
                        (e) =>
                            Boolean(e.key) &&
                            e.storageArea === storageArea &&
                            this.isStorageKey(e.key),
                    ),
                    map(
                        (e): StorageChange => ({
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                            key: this.revertStorageKey(e.key!),
                            value: e.newValue,
                            external: true,
                        }),
                    ),
                )
                .subscribe((change) => this.emitChange(change));
        } else {
            this.originalSetItem = noop;
            this.originalRemoveItem = noop;
        }

        this.calculateAvailableSpace();
    }

    get(key: string): string | null {
        const rawKey = this.getStorageKey(key);

        if (this.isNativeStorage) {
            return this.storageArea.getItem(rawKey);
        }

        if (this.pulseStorageBackend) {
            return this.pulseStorageBackend.get(rawKey);
        }

        return null;
    }

    set(key: string, value: string): void {
        const rawKey = this.getStorageKey(key);
        try {
            if (this.isNativeStorage) {
                const newItemSize = new TextEncoder().encode(value).length;
                const enoughSpace =
                    this.availableSpace !== null &&
                    this.availableSpace !== undefined &&
                    this.availableSpace - newItemSize > 100;

                if (
                    enoughSpace ||
                    this.availableSpace === null ||
                    this.availableSpace === undefined
                ) {
                    this.runWithoutHookEvents(() => this.originalSetItem(rawKey, value));
                    this.emitChange({ key, value, external: false });
                } else {
                    this.freeSpace();
                }
            } else if (this.pulseStorageBackend) {
                this.pulseStorageBackend.set(rawKey, value);
                this.emitChange({ key, value, external: false });
            }
        } catch (err) {
            this.freeSpace();
            console.warn(err);
        } finally {
            this.calculateAvailableSpace();
        }
    }

    keys(): string[] {
        if (this.isNativeStorage) {
            return Object.keys(this.storageArea)
                .filter((key) => this.isStorageKey(key))
                .map((key) => this.revertStorageKey(key));
        }

        if (this.pulseStorageBackend) {
            return this.pulseStorageBackend
                .keys()
                .filter((key) => this.isStorageKey(key))
                .map((key) => this.revertStorageKey(key));
        }

        return [];
    }

    remove(key: string): void {
        const rawKey = this.getStorageKey(key);

        if (this.isNativeStorage) {
            this.runWithoutHookEvents(() => this.originalRemoveItem(rawKey));
        } else if (this.pulseStorageBackend) {
            this.pulseStorageBackend.remove(rawKey);
        }
        this.emitChange({ key, value: null, external: false });
    }

    clear() {
        const keys = this.keys();

        if (this.isNativeStorage) {
            for (const key of keys) {
                this.runWithoutHookEvents(() => this.originalRemoveItem(this.getStorageKey(key)));
                this.emitChange({ key, value: null, external: false });
            }
        } else if (this.pulseStorageBackend) {
            for (const key of keys) {
                this.pulseStorageBackend.remove(this.getStorageKey(key));
                this.emitChange({ key, value: null, external: false });
            }
        }
    }

    all(): Record<string, string> {
        if (this.isNativeStorage) {
            return Object.keys(this.storageArea)
                .filter((key) => this.isStorageKey(key))
                .reduce<Record<string, string>>((data, key) => {
                    const revertedKey = this.revertStorageKey(key);
                    const value = this.storageArea.getItem(key);

                    if (typeof value === 'string') {
                        data[revertedKey] = value;
                    }

                    return data;
                }, {});
        }

        if (this.pulseStorageBackend) {
            const rawAll = this.pulseStorageBackend.all();

            return Object.keys(rawAll)
                .filter((key) => this.isStorageKey(key))
                .reduce<Record<string, string>>((data, key) => {
                    data[this.revertStorageKey(key)] = rawAll[key];

                    return data;
                }, {});
        }

        return {};
    }

    freeSpace() {
        this.freeSpace$.next();
    }

    private runWithoutHookEvents(action: () => void): void {
        this.suppressHookEvents++;
        try {
            action();
        } finally {
            this.suppressHookEvents--;
        }
    }

    async calculateAvailableSpace() {
        this.availableSpace = await this.quote.quoteSize();
    }
}
